import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import KeyboardArrowUpOutlinedIcon from "@mui/icons-material/KeyboardArrowUpOutlined";
import { Box, Menu, MenuItem, Typography } from "@mui/material";
import React, { useState } from "react";

import Highlight from "../../../../Highlight";
import { Worker } from "../../../store/full-time-shifts-type";

interface StatusOption {
  id: number;
  label: string;
  action: (worker: Worker) => void;
}

interface StatusChipProps {
  worker: Worker;
  search: string;
  hasPermission: boolean;
  getAvailableStatusOptions: (currentCode: string, isMoved: boolean, originalStatus: string) => StatusOption[];
  statusChipStyles: Record<string, any>;
}

export const StatusChip: React.FC<StatusChipProps> = ({
  worker,
  search,
  hasPermission,
  getAvailableStatusOptions,
  statusChipStyles,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!hasPermission) return;
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const statusOptions = getAvailableStatusOptions(
    worker.status.code,
    worker.moved,
    worker.originalStatus?.code ?? "SCH"
  );

  const style = {
    ...statusChipStyles[worker.status.code],
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "12px",
    width: "fit-content",
    height: "23px",
    border: worker.moved ? "1px dashed rgba(0,0,0,0.6)" : "1px solid transparent",
    padding: hasPermission ? "0px 8px 0px 12px" : "0px 12px",
    gap: "4px",
    cursor: hasPermission ? "pointer" : "default",
  };

  return (
    <Box
      key={`${worker.id}_${worker.status.id}`}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        justifyContent: "center",
        height: "58px",
        gap: "2px",
      }}
    >
      <Box
        sx={style}
        onClick={handleClick}
      >
        <Typography sx={{ fontSize: "13px", fontFamily: "Roboto" }}>
          <Highlight searchText={search} value={worker.status.label} />
        </Typography>
        {hasPermission && (
          <>
            {anchorEl === null ? (
              <KeyboardArrowDownOutlinedIcon
                sx={{
                  fontSize: "13px",
                  color: "rgba(0,0,0,0.54)",
                }}
              />
            ) : (
              <KeyboardArrowUpOutlinedIcon
                sx={{
                  fontSize: "13px",
                  color: "rgba(0,0,0,0.54)",
                }}
              />
            )}
          </>
        )}
      </Box>

      <Typography
        sx={{
          fontSize: "13px",
          fontFamily: "Roboto",
          color: "rgba(0,0,0,0.6)",
          marginLeft: "4px",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          overflow: "hidden",
          maxWidth: "100%",
          userSelect: "none",
        }}
      >
        <Highlight searchText={search} value={worker.note} />
      </Typography>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{
          list: {
            dense: true
          }
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        {statusOptions.map((option) => (
          <MenuItem
            key={option.id}
            onClick={() => {
              handleClose();
              option.action(worker);
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};
